import { useCallback, Fragment, useMemo, ReactNode, ChangeEvent } from 'react'
import { values, identity } from 'ramda'
import cn from 'classnames/bind'

import PlainFieldset from '../plain-fieldset'
import PlainRadioButton from './plain-radio-button'

import styles from './plain-radio-group.module.scss'

const cx = cn.bind(styles)

export type Item = {
  id: string
  title: string | ReactNode
  disabled?: boolean
  level?: number
}

export type Props = {
  id: string
  className?: string
  label?: ReactNode
  current?: string
  disabled?: boolean
  keepPadding?: boolean
  items: { [key: string]: Item } | Array<Item>
  afterContent?: ReactNode
  skipUpperSpace?: boolean
  skipVerticalSpaces?: boolean
  inlineItems?: boolean
  onChange?: (id: string) => void
  mapItemPropsBeforeRender?: (item: Item) => Item
}

const PlainRadioGroup = ({
  id,
  className,
  label,
  current,
  disabled,
  keepPadding,
  items,
  afterContent,
  skipUpperSpace,
  skipVerticalSpaces,
  inlineItems,
  onChange,
  mapItemPropsBeforeRender = identity,
}: Props) => {
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value, checked } = e.currentTarget
      if (checked && onChange) {
        onChange(value)
      }
    },
    [onChange],
  )

  const itemsList = useMemo(() => {
    return items instanceof Array ? items : values(items)
  }, [items])

  const renderItems = itemsList.map((itemProps) => {
    const { id: itemId, title, disabled: dd, level } = mapItemPropsBeforeRender(itemProps)
    return (
      <PlainRadioButton
        key={itemId}
        className={cx({ inlineItems })}
        label={title}
        value={itemId}
        name={id}
        disabled={disabled || dd}
        onChange={handleChange}
        checked={itemId === current}
        level={level}
      />
    )
  })

  if (label) {
    const legend = typeof label === 'string' ? label : <span>{label}</span>

    return (
      <Fragment>
        <PlainFieldset
          legend={legend}
          name={id}
          skipUpperSpace={skipUpperSpace}
          skipVerticalSpaces={skipVerticalSpaces}
          className={cx(styles.root, className)}
          legendClassName={styles.legend}
          strong
        >
          {renderItems}
        </PlainFieldset>
        {afterContent}
      </Fragment>
    )
  }

  return (
    <div
      className={cx(
        styles.rootUngroupped,
        {
          keepPadding,
          skipUpperSpace,
          skipVerticalSpaces,
        },
        className,
      )}
    >
      {renderItems}
      {afterContent}
    </div>
  )
}

export default PlainRadioGroup
